import React, { useCallback, useEffect, useState } from 'react';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import {
  ConnectAccountOnboarding,
  ConnectAccountManagement,
  ConnectComponentsProvider,
} from '@stripe/react-connect-js';
import toast from 'react-hot-toast';
import { Loader2, AlertTriangle, CreditCard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCurrentAcademy } from '../../../../hooks/useDataStore';
import { academyService } from '../../../../services/academy';
import {
  createConnectedAccount,
  createConnectedAccountForOrganization,
  getAccountStatus,
  createAccountSession,
  type AccountStatusResponse,
} from '../../../../services/connect';
import { auth } from '../../../../config/firebase';
import { STRIPE_PUBLISHABLE_KEY } from '../../../../config/api';
import {
  Button,
  Card,
  CardHeader,
  CardContent,
  Badge,
  Switch,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Checkbox,
  Input,
  Textarea,
} from '../../../../components/ui';
import {
  getSurchargeWarning,
  SURCHARGE_ACKNOWLEDGMENT_TEXT,
  PROCESSING_TAX_DISCLOSURE,
  TierFeatureKey,
  type Academy,
  type Club,
  type Organization,
  type Region,
} from '@court-sync/shared';
import { clubService } from '../../../../services/club';
import { useManagementDashboardFeatureAccess } from '../../../../hooks/useFeatureAccess';
import { useResolvedThemeIsDark } from '../../../../store/theme';
import {
  applyStripeConnectAppearance,
  getStripeConnectAppearanceVariables,
} from '../../../../utils/stripeConnectAppearance';

const SURCHARGE_PASS_THROUGH_DESCRIPTION =
  'Add Stripe card processing fees to checkout totals for online lesson, court hire, and membership payments.';

export interface PaymentsSettingsTabProps {
  academy: Academy | null;
  organization?: Organization | null;
  organizationName?: string;
  organizationRegion?: Region | null;
  lessonConnectSupported?: boolean;
  allowClubConnect?: boolean;
  club?: Club | null;
  /** When true, show a prominent Connect setup CTA and hide post-connect settings until ready. */
  setupFocusMode?: boolean;
  onAcademyUpdated?: () => void;
  onClubUpdated?: (club: Club) => void;
  onConnectAccountUpdated?: () => void;
  stripeConnectInstance?: Awaited<ReturnType<typeof loadConnectAndInitialize>> | null;
}

export default function PaymentsSettingsTab({
  academy,
  organization = null,
  organizationName,
  organizationRegion = null,
  lessonConnectSupported = true,
  allowClubConnect = false,
  club = null,
  setupFocusMode = false,
  onAcademyUpdated,
  onClubUpdated,
  onConnectAccountUpdated,
  stripeConnectInstance = null,
}: PaymentsSettingsTabProps) {
  const navigate = useNavigate();
  const { loadCurrentAcademy, loadUserAcademies } = useCurrentAcademy();
  const { hasAccess: hasPaymentAccess, tierResolved: paymentAccessTierResolved } =
    useManagementDashboardFeatureAccess(TierFeatureKey.PAYMENT_PROCESSING);
  const isDark = useResolvedThemeIsDark();

  const [connectAccountId, setConnectAccountId] = useState<string | null>(null);
  const [accountStatus, setAccountStatus] = useState<AccountStatusResponse | null>(null);
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [onboardingConnectInstance, setOnboardingConnectInstance] = useState<Awaited<
    ReturnType<typeof loadConnectAndInitialize>
  > | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [detectedCountry, setDetectedCountry] = useState<{ code: Region; name: string } | null>(null);
  const [paymentCollectionMessage, setPaymentCollectionMessage] = useState('');
  const [savingPaymentMessage, setSavingPaymentMessage] = useState(false);
  const [surchargePassThroughEnabled, setSurchargePassThroughEnabled] = useState(false);
  const [clubMembershipSurchargeEnabled, setClubMembershipSurchargeEnabled] = useState(false);
  const [surchargeConfirmOpen, setSurchargeConfirmOpen] = useState(false);
  const [surchargeConfirmTarget, setSurchargeConfirmTarget] = useState<'academy' | 'club'>('academy');
  const [surchargeAcknowledged, setSurchargeAcknowledged] = useState(false);
  const [savingSurcharge, setSavingSurcharge] = useState(false);
  const [legalBusinessName, setLegalBusinessName] = useState('');
  const [receiptNotes, setReceiptNotes] = useState('');
  const [savingReceiptFields, setSavingReceiptFields] = useState(false);

  const syncFromAcademy = useCallback((next: Academy | null) => {
    setConnectAccountId(next?.stripeConnectAccountId ?? null);
    setPaymentCollectionMessage(next?.paymentCollectionMessage ?? '');
    setSurchargePassThroughEnabled(next?.surchargePassThroughEnabled === true);
    setLegalBusinessName(next?.legalBusinessName ?? next?.name ?? '');
    setReceiptNotes(next?.receiptNotes ?? '');
  }, []);

  const syncFromOrganization = useCallback((next: Organization | null) => {
    setConnectAccountId(next?.stripeConnectAccountId ?? null);
    setLegalBusinessName(next?.name ?? organizationName ?? '');
  }, [organizationName]);

  useEffect(() => {
    if (academy) {
      syncFromAcademy(academy);
      return;
    }
    syncFromOrganization(organization);
  }, [academy, organization, syncFromAcademy, syncFromOrganization]);

  useEffect(() => {
    setClubMembershipSurchargeEnabled(club?.membershipSurchargeEnabled === true);
  }, [club]);

  useEffect(() => {
    if (connectAccountId) {
      getAccountStatus(connectAccountId)
        .then(setAccountStatus)
        .catch(() => setAccountStatus(null));
    } else {
      setAccountStatus(null);
    }
  }, [connectAccountId]);

  useEffect(() => {
    if (!connectAccountId) {
      import('../../../../services/regionService')
        .then(({ regionService }) => regionService.detectRegion())
        .then((region) => {
          setDetectedCountry({
            code: region,
            name: region === 'AU' ? 'Australia' : region === 'GB' ? 'United Kingdom' : 'United States',
          });
        })
        .catch(() => setDetectedCountry({ code: 'AU', name: 'Australia' }));
    }
  }, [connectAccountId]);

  const initializeStripeConnect = useCallback(async () => {
    if (!connectAccountId) return;
    const publishableKey = STRIPE_PUBLISHABLE_KEY;
    if (!publishableKey) {
      toast.error('Stripe publishable key not configured');
      return;
    }
    const instance = await loadConnectAndInitialize({
      publishableKey,
      fetchClientSecret: async () => {
        const response = await createAccountSession(connectAccountId, {
          account_onboarding: { enabled: true },
        });
        return response.clientSecret;
      },
      appearance: { variables: getStripeConnectAppearanceVariables(isDark) },
    });
    setOnboardingConnectInstance(instance);
  }, [connectAccountId, isDark]);

  useEffect(() => {
    if (connectAccountId && showOnboarding) {
      initializeStripeConnect().catch(() => toast.error('Failed to initialize Stripe Connect'));
    }
  }, [connectAccountId, showOnboarding, initializeStripeConnect]);

  useEffect(() => {
    applyStripeConnectAppearance(onboardingConnectInstance, isDark);
  }, [isDark, onboardingConnectInstance]);

  const handleRefreshStatus = async () => {
    if (!connectAccountId) return;
    try {
      const status = await getAccountStatus(connectAccountId);
      setAccountStatus(status);
      onAcademyUpdated?.();
    } catch {
      toast.error('Failed to refresh status');
    }
  };

  const handleCreateConnectAccount = async () => {
    const businessLabel = academy?.name ?? organizationName ?? organization?.name;
    if (!businessLabel) {
      toast.error('Organization information not found');
      return;
    }
    const email = auth.currentUser?.email;
    if (!email) {
      toast.error('User email not found');
      return;
    }
    setCreatingAccount(true);
    try {
      const countryCode =
        detectedCountry?.code || academy?.region || organizationRegion || organization?.region || 'AU';
      const result = academy?.id
        ? await createConnectedAccount(academy.id, email, businessLabel, countryCode)
        : organization?.id
          ? await createConnectedAccountForOrganization(
              organization.id,
              email,
              businessLabel,
              countryCode
            )
          : null;
      if (!result) {
        toast.error('Organization information not found');
        return;
      }
      setConnectAccountId(result.accountId);
      setShowOnboarding(true);
      toast.success('Connect account created. Complete onboarding below.');
      if (academy?.id) {
        await loadCurrentAcademy(academy.id, { force: true });
        await loadUserAcademies({ force: true });
      }
      onAcademyUpdated?.();
      onConnectAccountUpdated?.();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to create connect account');
    } finally {
      setCreatingAccount(false);
    }
  };

  const handleSavePaymentMessage = async () => {
    if (!academy?.id) return;
    setSavingPaymentMessage(true);
    try {
      await academyService.updateAcademy(academy.id, {
        paymentCollectionMessage: paymentCollectionMessage || undefined,
      });
      await loadCurrentAcademy(academy.id, { force: true });
      await loadUserAcademies({ force: true });
      toast.success('Payment collection message saved');
      onAcademyUpdated?.();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to save');
    } finally {
      setSavingPaymentMessage(false);
    }
  };

  const handleSaveSurchargePassThrough = async (value: boolean) => {
    if (!academy?.id) return;
    setSavingSurcharge(true);
    try {
      await academyService.updateAcademy(academy.id, {
        surchargePassThroughEnabled: value,
        ...(value ? { acknowledgeSurchargePassThrough: true as const } : {}),
      });
      setSurchargePassThroughEnabled(value);
      setSurchargeConfirmOpen(false);
      setSurchargeAcknowledged(false);
      toast.success(`Processing fee pass-through ${value ? 'enabled' : 'disabled'}`);
      onAcademyUpdated?.();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to save surcharge setting');
    } finally {
      setSavingSurcharge(false);
    }
  };

  const handleSaveClubMembershipSurcharge = async (value: boolean) => {
    if (!club?.id) return;
    setSavingSurcharge(true);
    try {
      const res = await clubService.updateClub(club.id, {
        membershipSurchargeEnabled: value,
        ...(value ? { acknowledgeSurchargePassThrough: true } : {}),
      });
      if (res.success && res.data) {
        setClubMembershipSurchargeEnabled(value);
        setSurchargeConfirmOpen(false);
        setSurchargeAcknowledged(false);
        toast.success(`Processing fee pass-through ${value ? 'enabled' : 'disabled'}`);
        onClubUpdated?.(res.data);
      } else {
        toast.error(res.error || 'Failed to save surcharge setting');
      }
    } finally {
      setSavingSurcharge(false);
    }
  };

  const handleConfirmSurchargeEnable = () => {
    if (surchargeConfirmTarget === 'club') {
      void handleSaveClubMembershipSurcharge(true);
      return;
    }
    void handleSaveSurchargePassThrough(true);
  };

  const handleSaveReceiptFields = async () => {
    if (!academy?.id) return;
    setSavingReceiptFields(true);
    try {
      await academyService.updateAcademy(academy.id, {
        legalBusinessName: legalBusinessName.trim() || null,
        receiptNotes: receiptNotes.trim() || null,
      });
      toast.success('Receipt details saved');
      onAcademyUpdated?.();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to save receipt details');
    } finally {
      setSavingReceiptFields(false);
    }
  };

  if (!academy && !organization) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-gray-600 dark:text-gray-400">
          Loading payment settings…
        </CardContent>
      </Card>
    );
  }

  const entityName = academy?.name ?? organizationName ?? organization?.name ?? 'your organization';
  const entityRegion = academy?.region ?? organizationRegion ?? organization?.region ?? 'US';
  const paymentProcessingAllowed = hasPaymentAccess || allowClubConnect;
  const warning = getSurchargeWarning(entityRegion || 'US', academy?.stateOrProvince);
  const isProhibited = warning.level === 'warning';
  const clubMarketConfigured = !!club?.region;
  const clubWarning = club?.region
    ? getSurchargeWarning(club.region, club.stateOrProvince)
    : null;
  const clubSurchargeProhibited = clubWarning?.level === 'warning';
  const showManualPaymentMessage =
    !connectAccountId && academy && (!paymentProcessingAllowed || !lessonConnectSupported);

  return (
    <div className="space-y-6">
      {!connectAccountId && hasPaymentAccess && !lessonConnectSupported && (
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Online payments</h2>
            <p className="text-gray-600 dark:text-gray-400">
              Stripe Connect is not available for your country yet.
            </p>
          </CardHeader>
          <CardContent>
            <div
              className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
              role="status"
              data-testid="stripe-connect-region-blocked"
            >
              You can still collect payments manually and add payment instructions below.
            </div>
          </CardContent>
        </Card>
      )}

      {!connectAccountId && !paymentProcessingAllowed && academy && (
        <Card className="border-2 border-blue-400 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20">
          <CardContent className="p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center mb-2">
                  <CreditCard className="w-8 h-8 text-blue-600 dark:text-blue-400 mr-3 flex-shrink-0" />
                  <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200">
                    Enable Online Payment Processing
                  </h3>
                </div>
                <p className="text-blue-800 dark:text-blue-300 mb-3">
                  Upgrade to <strong>Professional</strong> to automatically collect payments when students
                  book lessons, plus tax configuration, sibling discounts, and promo codes for{' '}
                  {entityName}.
                </p>
                <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
                  <li>✓ Automatic payment collection via Stripe</li>
                  <li>✓ Tax configuration on the Tax tab after onboarding</li>
                  <li>✓ Sibling discounts and promo codes</li>
                  <li>✓ Bookings confirmed automatically upon payment</li>
                  <li>✓ Direct payouts to your bank account</li>
                </ul>
              </div>
              <div className="flex shrink-0 sm:pt-1">
                <Button
                  onClick={() => navigate('/settings?tab=subscription')}
                  disabled={!paymentAccessTierResolved}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white whitespace-nowrap sm:w-auto"
                >
                  Upgrade to Professional
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {!connectAccountId && paymentProcessingAllowed && lessonConnectSupported && (
        setupFocusMode ? (
          <Card className="border-2 border-violet-200 bg-gradient-to-br from-violet-50 via-white to-indigo-50 shadow-sm dark:border-violet-800/60 dark:from-violet-950/40 dark:via-gray-900 dark:to-indigo-950/30">
            <CardContent className="p-8 sm:p-10">
              <div className="mx-auto max-w-2xl text-center">
                <div className="mb-4 flex justify-center">
                  <div className="flex h-20 w-20 items-center justify-center rounded-full bg-violet-100 dark:bg-violet-900/30">
                    <CreditCard className="h-10 w-10 text-violet-500 dark:text-violet-400" aria-hidden />
                  </div>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
                  Set up Stripe Connect
                </h2>
                <p className="mt-2 text-gray-600 dark:text-gray-400">
                  Set up payment processing for {entityName}
                </p>
                <p className="mx-auto mt-4 max-w-lg text-sm text-gray-700 dark:text-gray-300">
                  {academy
                    ? 'Create a Stripe Connect account to accept online lesson payments. Tax configuration is managed separately after onboarding.'
                    : 'Create a Stripe Connect account to accept court hire and membership payments online. Tax configuration is managed separately after onboarding.'}
                </p>
                <ul className="mx-auto mt-6 max-w-md space-y-2 text-left text-sm text-gray-700 dark:text-gray-300">
                  {academy ? (
                    <>
                      <li className="flex items-start gap-2">
                        <span className="text-violet-600 dark:text-violet-400" aria-hidden>✓</span>
                        Accept card payments when students book lessons
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-violet-600 dark:text-violet-400" aria-hidden>✓</span>
                        Sibling discounts, promo codes, and payouts to your bank
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-violet-600 dark:text-violet-400" aria-hidden>✓</span>
                        Tax settings unlock after onboarding
                      </li>
                    </>
                  ) : (
                    <>
                      <li className="flex items-start gap-2">
                        <span className="text-violet-600 dark:text-violet-400" aria-hidden>✓</span>
                        Accept court hire and membership payments online
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-violet-600 dark:text-violet-400" aria-hidden>✓</span>
                        Manage payouts to your bank account
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-violet-600 dark:text-violet-400" aria-hidden>✓</span>
                        Unlock tax settings and membership discounts
                      </li>
                    </>
                  )}
                </ul>
                {detectedCountry && (
                  <p className="mt-6 text-sm text-violet-800 dark:text-violet-200">
                    Detected region: {detectedCountry.name} ({detectedCountry.code})
                  </p>
                )}
                <div className="mt-8">
                  <Button
                    size="lg"
                    onClick={handleCreateConnectAccount}
                    disabled={creatingAccount || !detectedCountry}
                    className="bg-violet-600 hover:bg-violet-700 text-white"
                  >
                    {creatingAccount ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating…
                      </>
                    ) : (
                      'Create Connect account'
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Stripe Connect</h2>
              <p className="text-gray-600 dark:text-gray-400">
                Set up payment processing for {entityName}
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {academy
                  ? 'Create a Stripe Connect account to accept online lesson payments. Tax configuration is managed separately on the Tax tab after onboarding.'
                  : 'Create a Stripe Connect account to accept court hire and membership payments online. Tax configuration is managed separately on the Tax tab after onboarding.'}
              </p>
              {detectedCountry && (
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  Detected region: {detectedCountry.name} ({detectedCountry.code})
                </p>
              )}
              <Button
                onClick={handleCreateConnectAccount}
                disabled={creatingAccount || !detectedCountry}
              >
                {creatingAccount ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Creating…
                  </>
                ) : (
                  'Create Connect account'
                )}
              </Button>
            </CardContent>
          </Card>
        )
      )}

      {showManualPaymentMessage && (
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Payment collection message
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {hasPaymentAccess
                ? 'Tell students and play event participants how to pay while online card payments are unavailable. This message is included in registration emails when payment is still due.'
                : 'Starter plans collect lesson and play event payments manually. Tell students how to pay your academy.'}
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label htmlFor="payment-collection-message" className="block text-sm font-medium mb-2">
                Instructions for students and participants
              </label>
              <Textarea
                id="payment-collection-message"
                value={paymentCollectionMessage}
                onChange={(e) => setPaymentCollectionMessage(e.target.value)}
                rows={4}
                placeholder="e.g. Bank transfer to BSB 123-456, account 12345678. Reference the student or event name."
              />
            </div>
            <Button onClick={handleSavePaymentMessage} disabled={savingPaymentMessage}>
              {savingPaymentMessage ? 'Saving…' : 'Save message'}
            </Button>
          </CardContent>
        </Card>
      )}

      {lessonConnectSupported &&
        connectAccountId &&
        accountStatus &&
        !accountStatus.chargesEnabled &&
        !showOnboarding && (
          setupFocusMode ? (
            <Card className="border-2 border-violet-200 bg-gradient-to-br from-violet-50 via-white to-indigo-50 shadow-sm dark:border-violet-800/60 dark:from-violet-950/40 dark:via-gray-900 dark:to-indigo-950/30">
              <CardContent className="p-8 sm:p-10">
                <div className="mx-auto max-w-2xl text-center">
                  <div className="mb-4 flex justify-center">
                    <div className="flex h-20 w-20 items-center justify-center rounded-full bg-violet-100 dark:bg-violet-900/30">
                      <CreditCard className="h-10 w-10 text-violet-500 dark:text-violet-400" aria-hidden />
                    </div>
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
                    Complete your Stripe setup
                  </h2>
                  <p className="mx-auto mt-4 max-w-lg text-sm text-gray-700 dark:text-gray-300">
                    Your Stripe Connect account is created but not fully activated yet. Finish onboarding
                    to start accepting online payments and unlock the payments dashboard.
                  </p>
                  <div className="mt-8">
                    <Button
                      size="lg"
                      onClick={() => setShowOnboarding(true)}
                      className="bg-violet-600 hover:bg-violet-700 text-white"
                    >
                      Complete setup
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Complete Stripe setup
                    </h2>
                    <Badge className="bg-orange-500">Setup required</Badge>
                  </div>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Add the remaining account details to start accepting online payments.
                  </p>
                </div>
                <Button className="shrink-0" onClick={() => setShowOnboarding(true)}>
                  Complete setup
                </Button>
              </CardContent>
            </Card>
          )
        )}

      {lessonConnectSupported && showOnboarding && connectAccountId && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <h2 className="text-xl font-semibold">Stripe Connect onboarding</h2>
            <Button variant="outline" size="sm" onClick={() => setShowOnboarding(false)}>
              Close
            </Button>
          </CardHeader>
          <CardContent>
            {!onboardingConnectInstance ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : (
              <div className="min-h-[600px]">
                <ConnectComponentsProvider connectInstance={onboardingConnectInstance}>
                  <ConnectAccountOnboarding
                    onExit={async () => {
                      setShowOnboarding(false);
                      await handleRefreshStatus();
                    }}
                  />
                </ConnectComponentsProvider>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {!setupFocusMode && lessonConnectSupported && connectAccountId && accountStatus?.chargesEnabled && hasPaymentAccess && (
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold">Processing fee pass-through</h2>
            <p className="text-gray-600 dark:text-gray-400">{SURCHARGE_PASS_THROUGH_DESCRIPTION}</p>
          </CardHeader>
          <CardContent className="space-y-4">
            {surchargePassThroughEnabled && isProhibited && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-lg p-3 flex gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800 dark:text-amber-200">{warning.message}</p>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Enable pass-through</span>
              <Switch
                checked={surchargePassThroughEnabled}
                onCheckedChange={(checked) => {
                  if (!checked) {
                    handleSaveSurchargePassThrough(false);
                  } else {
                    setSurchargeConfirmTarget('academy');
                    setSurchargeAcknowledged(false);
                    setSurchargeConfirmOpen(true);
                  }
                }}
              />
            </div>
            {surchargePassThroughEnabled && (
              <p className="text-sm text-gray-600 dark:text-gray-400">{PROCESSING_TAX_DISCLOSURE}</p>
            )}
          </CardContent>
        </Card>
      )}

      {!setupFocusMode &&
        lessonConnectSupported &&
        connectAccountId &&
        accountStatus?.chargesEnabled &&
        stripeConnectInstance &&
        !showOnboarding && (
          <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
            <Card>
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Stripe account</h2>
                    <p className="text-sm font-mono text-gray-500">{connectAccountId}</p>
                  </div>
                  <Badge className="bg-green-500">Active</Badge>
                </div>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <ConnectAccountManagement />
              </CardContent>
            </Card>
          </ConnectComponentsProvider>
        )}

      {!setupFocusMode &&
        allowClubConnect &&
        club &&
        connectAccountId &&
        accountStatus?.chargesEnabled && (
          <Card>
            <CardHeader>
              <h2 className="text-xl font-semibold">Processing fee pass-through</h2>
              <p className="text-gray-600 dark:text-gray-400">{SURCHARGE_PASS_THROUGH_DESCRIPTION}</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {!clubMarketConfigured ? (
                <div
                  className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
                  role="status"
                >
                  Set a club region in Club settings → Memberships to enable pass-through.
                </div>
              ) : null}
              {clubMembershipSurchargeEnabled && clubSurchargeProhibited && clubWarning ? (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-lg p-3 flex gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800 dark:text-amber-200">{clubWarning.message}</p>
                </div>
              ) : null}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Enable pass-through</span>
                <Switch
                  checked={clubMembershipSurchargeEnabled}
                  disabled={!clubMarketConfigured || savingSurcharge}
                  onCheckedChange={(checked) => {
                    if (!checked) {
                      void handleSaveClubMembershipSurcharge(false);
                    } else {
                      setSurchargeConfirmTarget('club');
                      setSurchargeAcknowledged(false);
                      setSurchargeConfirmOpen(true);
                    }
                  }}
                />
              </div>
              {clubMembershipSurchargeEnabled ? (
                <p className="text-sm text-gray-600 dark:text-gray-400">{PROCESSING_TAX_DISCLOSURE}</p>
              ) : null}
            </CardContent>
          </Card>
        )}

      {!setupFocusMode && lessonConnectSupported && connectAccountId && !showOnboarding && (
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold">Receipt details</h2>
            <p className="text-gray-600 dark:text-gray-400">
              Shown on payment receipt emails. Tax calculation and registration are configured on the Tax tab
              via Stripe Tax.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Legal business name</label>
              <Input value={legalBusinessName} onChange={(e) => setLegalBusinessName(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Receipt notes</label>
              <Textarea
                value={receiptNotes}
                onChange={(e) => setReceiptNotes(e.target.value)}
                rows={3}
                placeholder="Optional footer on payment receipts"
              />
            </div>
            <Button onClick={handleSaveReceiptFields} disabled={savingReceiptFields}>
              {savingReceiptFields ? 'Saving…' : 'Save receipt details'}
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={surchargeConfirmOpen} onOpenChange={setSurchargeConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enable processing fee pass-through</DialogTitle>
            <DialogDescription>{SURCHARGE_ACKNOWLEDGMENT_TEXT}</DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-2 py-2">
            <Checkbox
              id="surcharge-ack"
              checked={surchargeAcknowledged}
              onCheckedChange={(v) => setSurchargeAcknowledged(v === true)}
            />
            <label htmlFor="surcharge-ack" className="text-sm text-gray-700 dark:text-gray-300">
              I understand and want to enable pass-through
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSurchargeConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!surchargeAcknowledged || savingSurcharge}
              onClick={handleConfirmSurchargeEnable}
            >
              Enable
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
